import { ClearOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { RouteListFilters } from '@/types/routes';

const TRISTATE_OPTIONS = [
  { value: 'any', labelKey: 'routes.library.filters.tristate.any' },
  { value: 'yes', labelKey: 'routes.library.filters.tristate.yes' },
  { value: 'no', labelKey: 'routes.library.filters.tristate.no' },
] as const;

type Tristate = (typeof TRISTATE_OPTIONS)[number]['value'];

interface RoutesLibraryFiltersProps {
  readonly filters: RouteListFilters;
  readonly onChange: (next: RouteListFilters) => void;
  readonly onReset: () => void;
  readonly onRefresh: () => void;
  readonly disabled?: boolean;
}

export default function RoutesLibraryFilters({
  filters,
  onChange,
  onReset,
  onRefresh,
  disabled = false,
}: RoutesLibraryFiltersProps) {
  const { t } = useTranslation();

  const setRange = (range: [Dayjs | null, Dayjs | null] | null) => {
    onChange({
      ...filters,
      periodFrom: range?.[0] ? range[0].toISOString() : null,
      periodTo: range?.[1] ? range[1].toISOString() : null,
    });
  };

  const setLength = (key: 'lengthMinKm' | 'lengthMaxKm', value: number | null) => {
    onChange({ ...filters, [key]: value });
  };

  const setTristate = (
    key: 'hasAppliedBarriers' | 'hasResidualBarriers',
    value: Tristate,
  ) => {
    onChange({
      ...filters,
      [key]: value === 'any' ? null : value === 'yes',
    });
  };

  const setSearch = (search: string) => {
    onChange({ ...filters, search });
  };

  const tristateOptions = TRISTATE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
  }));

  const rangeValue: [Dayjs, Dayjs] | undefined =
    filters.periodFrom && filters.periodTo
      ? [dayjs(filters.periodFrom), dayjs(filters.periodTo)]
      : undefined;

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Row gutter={[8, 8]} align="middle">
        <Col flex="auto">
          <Input.Search
            allowClear
            placeholder={t('routes.library.filters.searchPlaceholder')}
            value={filters.search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={disabled}
          />
        </Col>
        <Col>
          <Space size={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              disabled={disabled}
              title={t('routes.library.filters.refresh')}
            />
            <Button
              icon={<ClearOutlined />}
              onClick={onReset}
              disabled={disabled}
              title={t('routes.library.filters.reset')}
            />
          </Space>
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('routes.library.filters.period')}
          </Typography.Text>
          <DatePicker.RangePicker
            value={rangeValue}
            onChange={setRange}
            disabled={disabled}
            style={{ width: '100%' }}
            allowEmpty={[true, true]}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('routes.library.filters.lengthMin')}
          </Typography.Text>
          <InputNumber
            value={filters.lengthMinKm ?? undefined}
            onChange={(value) => setLength('lengthMinKm', value as number | null)}
            min={0}
            step={10}
            disabled={disabled}
            style={{ width: '100%' }}
            addonAfter={t('common.units.kilometers')}
          />
        </Col>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('routes.library.filters.lengthMax')}
          </Typography.Text>
          <InputNumber
            value={filters.lengthMaxKm ?? undefined}
            onChange={(value) => setLength('lengthMaxKm', value as number | null)}
            min={0}
            step={10}
            disabled={disabled}
            style={{ width: '100%' }}
            addonAfter={t('common.units.kilometers')}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('routes.library.filters.applied')}
          </Typography.Text>
          <Select
            value={tristateValue(filters.hasAppliedBarriers)}
            onChange={(value: Tristate) => setTristate('hasAppliedBarriers', value)}
            options={tristateOptions}
            disabled={disabled}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={12}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('routes.library.filters.residual')}
          </Typography.Text>
          <Select
            value={tristateValue(filters.hasResidualBarriers)}
            onChange={(value: Tristate) => setTristate('hasResidualBarriers', value)}
            options={tristateOptions}
            disabled={disabled}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
    </Space>
  );
}

function tristateValue(value: boolean | null): Tristate {
  if (value === null) return 'any';
  return value ? 'yes' : 'no';
}
